import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useTheme } from '@/platform/design-system/hooks/useTheme';

export type WorkflowGroup =
  'detect-resolve' | 'discover-deliver' | 'observe-improve' | 'insight' | 'admin';

export interface AccentPalette {
  accent: string;
  accentHover: string;
  accentMuted: string;
  accentText: string;
  accentBorder: string;
  accentRing: string;
}

export interface WorkflowGroupInfo {
  group: WorkflowGroup;
  label: string;
  color: string;
  tailwindColor: string;
  palette: AccentPalette;
}

// All accent values must pass WCAG AA 4.5:1 contrast with white text
// (since .btn-accent uses 'bg-accent text-white'). The colour scale is
// roughly Tailwind 700-shade or darker:
//   #B45309 amber-700  : L=0.16  4.97:1 vs white  PASS
//   #6D28D9 violet-700 : L=0.10  6.95:1 vs white  PASS
//   #047857 emerald-700: L=0.17  4.76:1 vs white  PASS  (use green-800 for ratio cushion)
//   #0E7490 cyan-700   : L=0.18  4.66:1 vs white  PASS  (borderline)
//   #1D4ED8 blue-700   : L=0.13  5.85:1 vs white  PASS
const LIGHT_PALETTES: Record<string, AccentPalette> = {
  'detect-resolve': {
    accent: '#B45309',
    accentHover: '#92400E',
    accentMuted: '#FEF3C7',
    accentText: '#92400E',
    accentBorder: '#FCD34D',
    accentRing: 'rgba(245,158,11,0.4)',
  },
  'discover-deliver': {
    accent: '#6D28D9',
    accentHover: '#5B21B6',
    accentMuted: '#EDE9FE',
    accentText: '#5B21B6',
    accentBorder: '#C4B5FD',
    accentRing: 'rgba(139,92,246,0.4)',
  },
  'observe-improve': {
    accent: '#047857',
    accentHover: '#065F46',
    accentMuted: '#D1FAE5',
    accentText: '#065F46',
    accentBorder: '#6EE7B7',
    accentRing: 'rgba(16,185,129,0.4)',
  },
  insight: {
    accent: '#0E7490',
    accentHover: '#155E75',
    accentMuted: '#CFFAFE',
    accentText: '#155E75',
    accentBorder: '#67E8F9',
    accentRing: 'rgba(6,182,212,0.4)',
  },
  admin: {
    accent: '#1D4ED8',
    accentHover: '#1E40AF',
    accentMuted: '#DBEAFE',
    accentText: '#1E40AF',
    accentBorder: '#93C5FD',
    accentRing: 'rgba(59,130,246,0.4)',
  },
};

// Dark-mode accents — same WCAG AA constraint as light-mode: white text on
// `accent` must reach >= 4.5:1. Use the same deep shades since the colour
// constraint is independent of UI theme. accentText (the muted-bg companion
// glyph colour) can stay bright since it sits on dark accentMuted surfaces.
// accentBorder is the active-tab/active-state visible indicator and must
// reach >= 3:1 (WCAG 1.4.11 non-text contrast) against the dark surface, so we
// use the brighter 400-500 shades here instead of the same dark accent value.
const DARK_PALETTES: Record<string, AccentPalette> = {
  'detect-resolve': {
    accent: '#B45309',
    accentHover: '#92400E',
    accentMuted: 'rgba(120,53,15,0.2)',
    accentText: '#FCD34D',
    accentBorder: '#FCD34D',
    accentRing: 'rgba(245,158,11,0.4)',
  },
  'discover-deliver': {
    accent: '#6D28D9',
    accentHover: '#5B21B6',
    accentMuted: 'rgba(88,28,135,0.2)',
    accentText: '#C4B5FD',
    accentBorder: '#C4B5FD',
    accentRing: 'rgba(139,92,246,0.4)',
  },
  'observe-improve': {
    accent: '#047857',
    accentHover: '#065F46',
    accentMuted: 'rgba(6,78,59,0.2)',
    accentText: '#6EE7B7',
    accentBorder: '#6EE7B7',
    accentRing: 'rgba(16,185,129,0.4)',
  },
  insight: {
    accent: '#0E7490',
    accentHover: '#155E75',
    accentMuted: 'rgba(22,78,99,0.2)',
    accentText: '#67E8F9',
    accentBorder: '#67E8F9',
    accentRing: 'rgba(6,182,212,0.4)',
  },
  admin: {
    accent: '#1D4ED8',
    accentHover: '#1E40AF',
    accentMuted: 'rgba(30,58,138,0.2)',
    accentText: '#93C5FD',
    accentBorder: '#93C5FD',
    accentRing: 'rgba(59,130,246,0.4)',
  },
};

const ROUTE_GROUPS: Array<{
  prefixes: string[];
  group: WorkflowGroup;
  label: string;
  color: string;
  tailwindColor: string;
}> = [
  {
    prefixes: [
      '/tickets',
      '/dispatch',
      '/monitoring',
      '/alerts',
      '/backups',
      '/security',
      '/insights/resolution-feed',
      '/sla',
      '/workitems',
      '/operations',
      '/calls',
    ],
    group: 'detect-resolve',
    label: 'Detect & Resolve',
    color: '#D97706',
    tailwindColor: 'serviceOperations',
  },
  {
    prefixes: [
      '/proposals',
      '/customers',
      '/contacts',
      '/contracts',
      '/billing',
      '/procurement',
      '/projects',
      '/pricing',
      '/service-catalog',
      '/customer-lifecycle',
      '/insights/delivery-pipeline',
      '/sales-pipeline',
      '/email-signatures',
      '/runway',
    ],
    group: 'discover-deliver',
    label: 'Discover & Deliver',
    color: '#7C3AED',
    tailwindColor: 'device',
  },
  {
    prefixes: [
      '/devices',
      '/patches',
      '/compliance',
      '/device-management',
      '/agents',
      '/remote',
      '/mjolnir',
      '/commands',
      '/drift',
      '/vendor',
      '/observe',
      '/insights/client-health',
      '/insights/cost-of-inaction',
      '/insights/automation',
      '/fleet',
      '/hygiene',
    ],
    group: 'observe-improve',
    label: 'Observe & Improve',
    color: '#059669',
    tailwindColor: 'success',
  },
  {
    prefixes: [
      '/insights/engineer-workload',
      '/insights/revenue',
      '/reports',
      '/m365',
      '/cloudkick',
      '/analytics',
    ],
    group: 'insight',
    label: 'Insight',
    color: '#0891B2',
    tailwindColor: 'customer-lifecycle',
  },
  {
    prefixes: [
      '/settings',
      '/admin',
      '/tenant-setup',
      '/tenant-governance',
      '/automation',
      '/identity-lifecycle',
      '/tenant-setup',
      '/vault',
      '/communications-migration',
      '/implementation',
      '/workbench',
    ],
    group: 'admin',
    label: 'Administration',
    color: '#2563EB',
    tailwindColor: 'brand',
  },
];

const DEFAULT_GROUP: WorkflowGroup = 'admin';

export function useWorkflowGroup(): WorkflowGroupInfo {
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const palettes = isDark ? DARK_PALETTES : LIGHT_PALETTES;

  return useMemo(() => {
    const path = location.pathname;
    for (const { prefixes, group, label, color, tailwindColor } of ROUTE_GROUPS) {
      if (prefixes.some((p) => path === p || path.startsWith(p + '/'))) {
        return { group, label, color, tailwindColor, palette: palettes[group] };
      }
    }
    return {
      group: DEFAULT_GROUP,
      label: 'Administration',
      color: '#2563EB',
      tailwindColor: 'brand',
      palette: palettes[DEFAULT_GROUP],
    };
  }, [location.pathname, palettes]);
}
